import React, { useMemo, useState } from "react";
import PrimaryModal from "../../UI/Modal/PrimaryModal";
import Loader from "../../UI/Loader/Loader";
import SuccessAlert from "../../UI/Alert/SuccessAlert";
import { useUserContext } from "../../../contexts/UserContext";
import { addTealiumAudienceStream } from "../../../utils/APIs";
import { SecondaryButton, PrimaryButton } from "../../UI";
import { useContentfulContext } from "../../../contexts/ContentfulContext";
import AgGridWrapper from "../../UI/AgGrid";

const AddToAdobeTargetModel = ({
  showAddToAdobeTargetModel,
  handleCloseModal,
  addToAdobeTargetModalData,
  getTealiumAudienceStreamData,
  atAudienceConfigRule,
  setSelectedRecords
}) => {
  const { userData } = useUserContext();
  const { content } = useContentfulContext();

  const [updatedData, setUpdatedData] = useState(addToAdobeTargetModalData || []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState();
  // const [gridApi, setGridApi] = useState(null);

  const handleSubmit = async (values) => {
    try {
      const validate = addToAdobeTargetModalData.filter((x) => !(!!x.at_audience_name))

      if(validate?.length > 0) {
        setError(content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.errors.FE137)
        return;
      }

      setLoading(true);
      await addTealiumAudienceStream(
        userData.token, 
        userData.selectedOrganization.org_token,
        { 
          tealium_audiences: addToAdobeTargetModalData,
          adobe_io_integration_id: userData?.defaultAdobeIO?._id,
          adobe_target_audience_config_rule_id: atAudienceConfigRule._id,
        }
      )
      setError("");
      setSuccess(true);
      getTealiumAudienceStreamData();
      setSelectedRecords([]);
      handleCloseModal();
    } catch (error) {
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    handleCloseModal();
  };

  const colDefs = [
    {
      headerName: content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.table_headers.as_audience_id,
      field: "as_audience_id",
      resizable: true,
      suppressMovable: true,
      editable: false
    },
    {
      headerName: content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.table_headers.as_audience_name,
      field: "as_audience_name",
      resizable: true,
      suppressMovable: true,
      editable: false
    },
    {
      headerName: content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.table_headers.at_audience_name,
      field: "at_audience_name",
      resizable: true,
      suppressMovable: true,
    },
    {
      headerName: content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.table_headers.at_audience_workspace,
      field: "workspace",
      resizable: true,
      suppressMovable: true,
    },
    {
      headerName: content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.table_headers.description,
      field: "description",
      resizable: true,
      suppressMovable: true,
    }
  ];

  const autoSizeStrategy = useMemo(() => {
    return {
      type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        { colId: "id", minWidth: 250 },
        { colId: "name", minWidth: 300 },
        { colId: "at_audience_id", minWidth: 150 },
        { colId: "at_audience_name", minWidth: 150 },
        { colId: "at_audience_workspace", minWidth: 150 },
        { colId: "updatedAt", minWidth: 150 },
        { colId: "last_edited_by", minWidth: 150 },
      ],
    };
  }, []);

  const onGridReady = (params) => {
    // setGridApi(params.api);
  };

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      editable: true,
      cellDataType: false,
    };
  }, []);

  const handleRowEditingStopped = (values) => {
    const temp = updatedData
    temp[values.rowIndex] = values.data;
    setUpdatedData(temp);
    setError("")
  }

  return (
    <>
      {success && (
        <SuccessAlert
          disappearTime={
            content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.AT_Config_Modal
              .success_timeout
          }
        >
          {content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.AT_Config_Modal.success}
        </SuccessAlert>
      )}
      {showAddToAdobeTargetModel && (
        <PrimaryModal
          title={content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.title}
          hideModal={handleClose}
          widthClass="max-w-6xl"
        >
          {loading && <Loader />}
          <div className="p-6 space-y-6">
            <p>{content.ADOBE_TARGET_AUDIENCE_CONFIG_RULE_INPUT.Add_to_AT_Model.description}</p>
            <div
              className={"ag-theme-quartz"}
              style={{ height: "100%", width: "100%" }}
            >
              <AgGridWrapper
                tableId="add-to-adobe-target-modal-table"
                rowData={addToAdobeTargetModalData}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                editType={'fullRow'}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
                rowSelection="multiple"
                suppressRowClickSelection
                stopEditingWhenCellsLoseFocus={true}
                onRowEditingStopped={handleRowEditingStopped}
              />
            </div>

            <p className="mt-2 text-sm text-red-5">{error}</p>
            <div className="flex flex-row-reverse gap-2">
              <PrimaryButton handleClick={handleSubmit}>Submit</PrimaryButton>
              <SecondaryButton handleClick={handleClose}>Cancel</SecondaryButton>
            </div>
          </div>
        </PrimaryModal>
      )}
    </>
  );
};

export default AddToAdobeTargetModel;
